/**
 * One tracked account's collection: which profile it collects through. The profile is the
 * whole configuration, so the page picks one, shows its periods, and links to its editor.
 */

import { Link } from '@tanstack/react-router';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { Page } from '../../../chrome/page/Page';
import { PageHeader } from '../../../chrome/page/PageHeader';
import { ErrorState, failureSentence, LoadingState } from '../../../chrome/state';
import { useAccountTracking, useWriteAccountTracking } from '../../campaign/accounts/api/queries';
import { CollectionChips } from '../../campaign/accounts/CollectionChips';
import { useCan } from '../../session/orgContext';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Field, FieldLabel } from '../../../ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { PROFILE_EDITOR_PATH } from '../paths';
import { useMonitoringProfiles } from './Catalogue';
import { trt } from './tracking-editor/messages';

export function TrackingEditor(props: {
  campaignId: string;
  accountId: string;
  tenantId: string;
}): ReactNode {
  const canWrite = useCan('control_plane.write');
  const tracking = useAccountTracking(props.campaignId, props.accountId);
  const profiles = useMonitoringProfiles(props.tenantId);
  const write = useWriteAccountTracking(props.campaignId, props.accountId);
  const selectId = useId();
  const [profileId, setProfileId] = useState<string | null>(null);

  useEffect(() => {
    if (tracking.data && profileId === null) setProfileId(tracking.data.profile_id);
  }, [tracking.data, profileId]);

  if (tracking.isError) {
    return <ErrorState failure={tracking.error.failure} onRetry={() => void tracking.refetch()} />;
  }
  if (tracking.isPending || profiles.isPending) return <LoadingState label={trt('loading')} />;

  const chosen = profiles.data?.items.find((profile) => profile.id === profileId) ?? null;
  const dirty = profileId !== null && profileId !== tracking.data.profile_id;

  return (
    <Page>
      <PageHeader
        title={trt('heading')}
        description={trt('lead')}
        actions={
          canWrite ? (
            <Button
              type="button"
              disabled={!dirty || write.isPending}
              onClick={() =>
                profileId !== null &&
                write.mutate(
                  { profile_id: profileId },
                  {
                    onSuccess: () => toast.success(trt('saved')),
                    onError: (error) => toast.error(failureSentence(error.failure)),
                  },
                )
              }
            >
              {trt('save')}
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardContent className="flex flex-col gap-4">
          <Field className="max-w-md">
            <FieldLabel htmlFor={selectId}>{trt('profile')}</FieldLabel>
            <Select
              value={profileId ?? ''}
              onValueChange={setProfileId}
              disabled={!canWrite || write.isPending}
            >
              <SelectTrigger id={selectId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(profiles.data?.items ?? []).map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <CollectionChips media={chosen?.media ?? null} />
          {chosen !== null && canWrite && (
            <Link
              to={PROFILE_EDITOR_PATH}
              params={{ tenantId: props.tenantId, profileId: chosen.id }}
              className="self-start text-sm underline underline-offset-2"
            >
              {trt('editProfile')}
            </Link>
          )}
        </CardContent>
      </Card>
    </Page>
  );
}
